import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { 
  FileText, Download, Loader2, Receipt, CheckCircle, 
  Calendar, DollarSign, User, Eye, X, ChevronLeft, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInvoicesList = ({ token }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [page]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/invoices?page=${page}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
      setTotalPages(response.data.pages || 1);
      setTotalRevenue(response.data.total_revenue || 0);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (invoice) => {
    setViewingInvoice(invoice.transaction_id);
    setLoadingDetails(true);
    try {
      const response = await axios.get(
        `${API}/admin/invoices/${invoice.transaction_id}/view?party=${invoice.party}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvoiceDetails(response.data);
    } catch (error) {
      console.error('View error:', error);
      toast.error('Erreur lors du chargement de la facture');
      setViewingInvoice(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatCurrency = (amount, currency = 'CAD') => {
    return new Intl.NumberFormat('fr-CA', { 
      style: 'currency', 
      currency: currency 
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('fr-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Invoice detail modal
  const InvoiceModal = () => {
    if (!viewingInvoice || !invoiceDetails) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-900">
              {invoiceDetails.party === 'buyer' ? 'Facture' : 'Reçu'} {invoiceDetails.invoice_number}
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setViewingInvoice(null); setInvoiceDetails(null); }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="text-center pb-4 border-b">
              <h2 className="text-2xl font-bold text-brand-800">MIDDLEMAN</h2>
              <p className="text-sm text-gray-500">Transferts de billets sécurisés</p>
            </div>
            
            {/* Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Date:</span>
                <p className="font-medium">{formatDate(invoiceDetails.date)}</p>
              </div>
              <div>
                <span className="text-gray-500">Référence:</span>
                <p className="font-medium">{invoiceDetails.reference}</p>
              </div>
              <div>
                <span className="text-gray-500">Client:</span>
                <p className="font-medium break-all">{invoiceDetails.client?.name || invoiceDetails.client?.email}</p>
              </div>
              <div>
                <span className="text-gray-500">N° Client:</span>
                <p className="font-medium">{invoiceDetails.client?.client_number || 'N/A'}</p>
              </div>
            </div>
            
            {/* Event details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Détails de la transaction</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Événement:</span>
                  <span className="font-medium">{invoiceDetails.event?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Prix des billets:</span>
                  <span className="font-medium">{formatCurrency(invoiceDetails.event?.price, invoiceDetails.pricing?.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Billets:</span>
                  <span className="font-medium">{invoiceDetails.event?.ticket_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Rôle:</span>
                  <span className="font-medium">{invoiceDetails.party_label}</span>
                </div>
              </div>
            </div>
            
            {/* Fee breakdown */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-brand-800 text-white p-3 flex justify-between text-sm font-semibold">
                <span>Description</span>
                <span>Montant</span>
              </div>
              <div className="p-3 flex justify-between text-sm border-b">
                <span>Transfert de billets sécurisé</span>
                <span className="font-medium">{formatCurrency(invoiceDetails.pricing?.transfer_fee, invoiceDetails.pricing?.currency)}</span>
              </div>
              <div className="bg-green-600 text-white p-3 flex justify-between font-bold">
                <span>TOTAL</span>
                <span>{formatCurrency(invoiceDetails.pricing?.total, invoiceDetails.pricing?.currency)}</span>
              </div>
            </div>
            
            {/* Status */}
            <div className="text-center">
              <span className="inline-flex items-center gap-2 text-green-600 font-bold text-lg">
                <CheckCircle className="h-5 w-5" />
                PAYÉ
              </span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="sticky bottom-0 bg-gray-50 border-t p-4 flex justify-end">
            <Button 
              variant="outline"
              onClick={() => { setViewingInvoice(null); setInvoiceDetails(null); }}
            >
              Fermer
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-600 font-medium">Total des frais de transfert perçus</p>
              <p className="text-3xl font-bold text-emerald-800">{formatCurrency(totalRevenue)}</p>
            </div>
            <div className="p-4 bg-emerald-100 rounded-full">
              <DollarSign className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-brand-600" />
            Factures et reçus
          </CardTitle>
          <CardDescription>
            Liste de toutes les factures générées pour les transferts terminés
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invoices.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium">N° Facture</th>
                      <th className="text-left p-3 font-medium">Date</th>
                      <th className="text-left p-3 font-medium">Client</th>
                      <th className="text-left p-3 font-medium">Événement</th>
                      <th className="text-left p-3 font-medium">Rôle</th>
                      <th className="text-right p-3 font-medium">Montant</th>
                      <th className="text-center p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice, idx) => (
                      <tr key={`${invoice.invoice_number}-${idx}`} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-mono text-xs">{invoice.invoice_number}</td>
                        <td className="p-3">{formatDate(invoice.date)}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="truncate max-w-[150px]">{invoice.client_email}</span>
                          </div>
                        </td>
                        <td className="p-3 truncate max-w-[200px]">{invoice.event_name}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            invoice.party === 'buyer' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {invoice.party_label}
                          </span>
                        </td>
                        <td className="p-3 text-right font-medium">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            onClick={() => handleView(invoice)}
                            disabled={loadingDetails && viewingInvoice === invoice.transaction_id}
                            variant="outline"
                            size="sm"
                            className="gap-1"
                          >
                            {loadingDetails && viewingInvoice === invoice.transaction_id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Eye className="h-3 w-3" />
                            )}
                            Voir
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-500">
                    Page {page} sur {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucune facture disponible
              </h3>
              <p className="text-sm text-gray-500">
                Les factures apparaîtront ici une fois les premiers transferts terminés.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      
      <InvoiceModal />
    </div>
  );
};

export default AdminInvoicesList;
